"use client";

import { Figtree } from "next/font/google";
import styles from "./styles.module.css";
import { BsSendFill } from "react-icons/bs";
import { useServerAction } from "zsa-react";
import { sendContactEmailAction } from "./handler";
import { FormEvent, useState } from "react";
import { useTranslations } from "next-intl";
import { MdErrorOutline } from "react-icons/md";
import { toast } from "react-toastify";

const figtree = Figtree({ weight: ["400", "500"], subsets: ["latin"] });

export default function ContactPage() {
  const t = useTranslations("ContactUs");
  const { execute, error } = useServerAction(sendContactEmailAction);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const [data, err] = await execute({
      first_name: firstName,
      last_name: lastName,
      email: email,
      message: message,
    });
    if (!err && (data === null || data === undefined)) {
      toast(t("successMessage"), { type: "success" });
      setFirstName("");
      setLastName("");
      setEmail("");
      setMessage("");
    }
    if (data) {
      toast(t("errorMessage"), { type: "error" });
    }
  }

  return (
    <main className={`${figtree.className} ${styles.main}`}>
      <div className={styles.contentWrapper}>
        <div className={styles.textsWrapper}>
          <h1 className={styles.title}>{t("title")}</h1>
          <h2 className={styles.subTitle}>{t("subTitle")}</h2>
          <div className={styles.descriptionWrapper}>
            <p className={`${styles.description} ${styles.mainDescription}`}>
              {t("firstDescription")}
            </p>
            <p className={`${styles.description} ${styles.mainDescription}`}>
              {t("thirdDescription")}
            </p>
            <p className={styles.description}>{t("secondDescription")}</p>
          </div>
        </div>
        <form
          className={styles.form}
          onSubmit={handleSubmit}
          role="form"
          aria-label={t("contactForm")}
        >
          <div className={styles.inputWrapper}>
            <label
              htmlFor="first_name"
              className={`${styles.fieldTitle} ${
                error?.fieldErrors?.first_name && styles.hasError
              }`}
            >
              {t("firstName")}
            </label>
            {error?.fieldErrors?.first_name && (
              <p className={styles.inputErrorText}>
                {t(error.fieldErrors.first_name[0] || "fieldRequired")}
                <span>
                  <MdErrorOutline aria-hidden="true" />
                </span>
              </p>
            )}
            <input
              className={`${styles.input} ${
                error?.fieldErrors?.first_name && styles.inputError
              }`}
              type="text"
              name="first_name"
              placeholder={t("firstNamePlaceholder")}
              maxLength={30}
              value={firstName}
              onChange={(e) => {
                e.preventDefault();
                setFirstName(e.target.value);
              }}
              aria-invalid={!!error?.fieldErrors?.first_name}
            />
          </div>
          <div className={styles.inputWrapper}>
            <label
              htmlFor="last_name"
              className={`${styles.fieldTitle} ${
                error?.fieldErrors?.last_name && styles.hasError
              }`}
            >
              {t("lastName")}
            </label>
            {error?.fieldErrors?.last_name && (
              <p className={styles.inputErrorText}>
                {t(error.fieldErrors.last_name[0] || "fieldRequired")}
                <span>
                  <MdErrorOutline aria-hidden="true" />
                </span>
              </p>
            )}
            <input
              className={`${styles.input} ${
                error?.fieldErrors?.last_name && styles.inputError
              }`}
              type="text"
              name="last_name"
              placeholder={t("lastNamePlaceholder")}
              maxLength={30}
              value={lastName}
              onChange={(e) => {
                e.preventDefault();
                setLastName(e.target.value);
              }}
              aria-invalid={!!error?.fieldErrors?.last_name}
            />
          </div>
          <div className={styles.inputWrapper}>
            <label
              htmlFor="email"
              className={`${styles.fieldTitle} ${
                error?.fieldErrors?.email && styles.hasError
              }`}
            >
              {t("email")}
            </label>
            {error?.fieldErrors?.email && (
              <p className={styles.inputErrorText}>
                {t(error.fieldErrors.email[0] || "fieldRequired")}
                <span>
                  <MdErrorOutline aria-hidden="true" />
                </span>
              </p>
            )}
            <input
              className={`${styles.input} ${
                error?.fieldErrors?.email && styles.inputError
              }`}
              type="email"
              name="email"
              placeholder={t("emailPlaceholder")}
              maxLength={50}
              value={email}
              onChange={(e) => {
                e.preventDefault();
                setEmail(e.target.value);
              }}
              aria-invalid={!!error?.fieldErrors?.email}
            />
          </div>
          <div className={styles.inputWrapper}>
            <label
              htmlFor="message"
              className={`${styles.fieldTitle} ${
                error?.fieldErrors?.message && styles.hasError
              }`}
            >
              {t("message")}
            </label>
            {error?.fieldErrors?.message && (
              <p className={styles.inputErrorText}>
                {t(error.fieldErrors.message[0] || "fieldRequired")}
                <span>
                  <MdErrorOutline aria-hidden="true" />
                </span>
              </p>
            )}
            <textarea
              className={`${styles.textArea} ${
                error?.fieldErrors?.message && styles.inputError
              }`}
              placeholder={t("messagePlaceholder")}
              name="message"
              maxLength={500}
              value={message}
              onChange={(e) => {
                e.preventDefault();
                setMessage(e.target.value);
              }}
              aria-invalid={!!error?.fieldErrors?.message}
            />
          </div>
          <div className={styles.sendButtonWrapper}>
            <button
              className={styles.sendButton}
              name="send"
              aria-label={t("sendMessage")}
            >
              {t("sendButton")}
              <span>
                <BsSendFill className={styles.icon} />
              </span>
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
